import { API_ORIGIN, apiUrl } from '../../../config/api';
import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserAuthContext } from "../../../UserAuthContext";
import { getGuestCart, removeGuestCartItem, syncGuestCart } from '../../../utils/guestCart';
import { ToastContainer, toast } from 'react-toastify';
import { Trash2 } from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';
import "./CartDropdown.css";
import { useCurrency } from '../../../contexts/CurrencyContext';

export default function CartDropdown() {
  const [open, setOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartUpdate, setCartUpdate] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useContext(UserAuthContext);
  const { formatMoney } = useCurrency();

  // Utility to construct image URL - improved for robustness (handles filename, /uploads/..., or full URLs)
  const getImageUrl = (filename) => {
    if (!filename) {
      return 'https://via.placeholder.com/150';
    }
    // If already a full URL, use it
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    // If starts with /uploads/ or uploads/, clean it
    let clean = filename.replace(/^\/?(uploads\/)?/i, '');
    return apiUrl(`/uploads/${clean}`);
  };

  // Fetch cart items from the database
  useEffect(() => {
    const fetchCartItems = async () => {
      if (!user) {
        setCartItems(getGuestCart().map(item => ({
          id: item.idproducts,
          name: item.product_name,
          description: item.product_description || '',
          image: item.product_image,
          quantity: item.quantity || 1,
          price: item.product_price,
          priceAvailable: item.price_available !== false,
          priceCurrency: item.price_currency || item.priceCurrency || 'USD',
          mpq: item.product_mpq || 1,
        })));
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setCartItems([]);
        return;
      }

      setIsLoading(true);
      try {
        await syncGuestCart(token);
        const response = await fetch(apiUrl('/api/cart'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Failed to fetch cart items:', errorData);
          throw new Error(errorData.message || 'Failed to fetch cart items');
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          console.error('API response is not an array:', data);
          throw new Error('Invalid API response format');
        }

        const mappedItems = data.map(item => ({
          id: item.idproducts,
          name: item.product_name,
          description: item.product_description,
          image: item.product_image,
          quantity: item.quantity || 1,
          price: item.product_price,
          priceAvailable: item.price_available !== false,
          priceCurrency: item.price_currency || item.priceCurrency || 'USD',
          mpq: item.product_mpq || 1, // Minimum Purchase Quantity for raw materials
        }));
        setCartItems(mappedItems);
      } catch (error) {
        console.error('Error fetching cart items:', error.message);
        toast.error(`Error fetching cart items: ${error.message}`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
        });
        setCartItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCartItems();
  }, [user, cartUpdate]);

  // Listen for cart update events
  useEffect(() => {
    const handleCartUpdate = () => {
      setCartUpdate(prev => prev + 1);
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle delete item
  const handleDeleteItem = async (idproducts, productName) => {
    if (!user) {
      removeGuestCartItem(idproducts);
      toast.success(`Removed ${productName} from cart!`, {
        position: "top-right",
        autoClose: 200,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      setCartUpdate(prev => prev + 1);
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expired. Please log in again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      navigate('/login');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/cart/${idproducts}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to delete cart item:', errorData);
        throw new Error(errorData.message || 'Failed to delete cart item');
      }

      toast.success(`Removed ${productName} from cart!`, {
        position: "top-right",
        autoClose: 200,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });

      setCartUpdate(prev => prev + 1);
      // Notify other components (e.g. cart page, navbar badge) about the change
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      console.error('Error deleting cart item:', error.message);
      toast.error('Failed to delete item from cart. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToCart = () => {
    setOpen(false);
    navigate("/cart");
  };

  const handleCartClick = () => {
    if (window.matchMedia('(max-width: 900px)').matches) {
      handleGoToCart();
      return;
    }

    setOpen((isOpen) => !isOpen);
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="eulua-header-cart" ref={dropdownRef}>
      <button
        className="eulua-header-cart-button"
        onClick={handleCartClick}
        aria-label={`Cart with ${totalItems} items`}
        disabled={isLoading}
      >
        <img
          src="https://cdn-icons-png.flaticon.com/512/1170/1170678.png"
          alt="Cart"
          className="eulua-header-cart-icon"
        />
        {totalItems > 0 && (
          <span className="eulua-header-cart-badge">{totalItems}</span>
        )}
      </button>

      {open && (
        <div className={`eulua-header-cart-menu ${open ? 'eulua-header-cart-menu--open' : ''}`}>
          {isLoading ? (
            <p className="eulua-header-cart-loading">Loading...</p>
          ) : cartItems.length === 0 ? (
            <div className="eulua-header-cart-empty">
              <img
                src="https://cdn-icons-png.flaticon.com/512/1642/1642605.png"
                alt="Empty cart"
                className="eulua-header-cart-empty-icon"
              />
              <p className="eulua-header-cart-empty-text">Empty cart!</p>
            </div>
          ) : (
            cartItems.map((product) => (
              <div className="eulua-header-cart-item" key={product.id}>
                <img
                  src={getImageUrl(product.image)}
                  alt={product.name}
                  className="eulua-header-cart-product-image"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/150';
                  }}
                />
                <div className="eulua-header-cart-product-text">
                  <span className="eulua-header-cart-product-name">{product.name}</span>
                  <span className="eulua-header-cart-product-description">
                    {product.description.length > 60
                      ? product.description.slice(0, 60) + "..."
                      : product.description}
                  </span>
                  <div className="eulua-header-cart-product-meta">
                    {product.quantity !== undefined && (
                      <span className="eulua-header-cart-product-quantity">Qty: {product.quantity}</span>
                    )}
                    {product.price !== undefined && (
                      <span className="eulua-header-cart-product-price">{product.priceAvailable === false ? 'Price on request' : formatMoney(product.price * product.quantity, product.priceCurrency)}</span>
                    )}
                  </div>
                  {product.mpq && product.mpq > 1 && (
                    <div style={{ fontSize: '10px', color: '#36546A', marginTop: '2px' }}>
                      Min. Qty: {product.mpq} units
                    </div>
                  )}
                </div>
                <button
                  className="eulua-header-cart-delete"
                  onClick={() => handleDeleteItem(product.id, product.name)}
                  aria-label={`Remove ${product.name} from cart`}
                  disabled={isLoading}
                >
                  <Trash2 className="eulua-header-cart-delete-icon" aria-hidden="true" />
                </button>
              </div>
            ))
          )}
          <button
            className="eulua-header-cart-checkout"
            onClick={handleGoToCart}
            aria-label="Go to cart page"
            disabled={isLoading}
          >
            Go to Cart
          </button>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}
